import { Environment, commitMutation } from 'relay-runtime'
import { graphql } from 'react-relay'
import { FormImageUploadMutation } from './__generated__/FormImageUploadMutation.graphql'

const CREATE_IMAGE_MUTATION = graphql`
  mutation FormImageUploadMutation($input: CreateImageInput!) {
    createImage(input: $input) {
      image {
        rowId
        url
      }
      uploadURL
    }
  }
`

import type { ImageContentType } from './__generated__/FormImageUploadMutation.graphql'

const CONTENT_TYPE_ENUM_MAPPING: Record<string, ImageContentType> = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
}

export type FormImageValue = {
  uploadImage: (
    ownerId: FormImageUploadMutation['variables']['input']['ownerId'],
    ownerType: FormImageUploadMutation['variables']['input']['ownerType']
  ) => Promise<FormImageUploadMutation['response'] | undefined>
  file?: File
}

export const createFormImage = (
  relayEnvironment: Environment,
  file: File
): FormImageValue | undefined => {
  const contentType = CONTENT_TYPE_ENUM_MAPPING[file.type]

  if (!contentType) {
    console.error('Could not determine content type', file.type)
    return
  }

  return {
    uploadImage: async (
      ownerId: FormImageUploadMutation['variables']['input']['ownerId'],
      ownerType: FormImageUploadMutation['variables']['input']['ownerType']
    ) => {
      const imageDimensions = await (async () => {
        const img = new Image()
        img.src = URL.createObjectURL(file)
        await img.decode()

        return { width: img.width, height: img.height }
      })()

      const mutationResponse = await new Promise<
        FormImageUploadMutation['response']
      >((resolve, reject) =>
        commitMutation<FormImageUploadMutation>(relayEnvironment, {
          mutation: CREATE_IMAGE_MUTATION,
          variables: {
            input: {
              contentType,
              ownerId,
              ownerType,
              width: imageDimensions.width,
              height: imageDimensions.height,
            },
          },
          onCompleted(response) {
            resolve(response)
          },
          onError(err) {
            reject(err)
          },
        })
      )

      if (
        !mutationResponse.createImage ||
        !mutationResponse.createImage.uploadURL
      ) {
        console.error(
          'Could not create image on server',
          mutationResponse.createImage
        )
        return
      }

      const imageUploadResponse = await fetch(
        mutationResponse.createImage.uploadURL,
        {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
          mode: 'cors',
        }
      )

      if (!imageUploadResponse.ok) {
        console.error('Could not upload image to AWS', imageUploadResponse)
        return
      }

      return mutationResponse
    },
    file,
  }
}
